// src/pages/SubmitProblem.js - PREMIUM MULTI-STEP VERSION
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { createProblem } from "../services/problemService";
import { TAG_CATALOG, normalizePredefinedTag } from "../utils/predefinedTags";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import {
  Brain,
  FileText,
  Paperclip,
  CheckCircle2,
  CreditCard,
  Check,
  Upload,
  Video,
  X,
  Lock,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import { detectOutOfScope } from "../utils/outOfScopeDetector";
import { calculateSessionPrice } from "../utils/pricingCalculator";
import { deriveSeverity } from "../constants/severity";
import { getLaneForCategory } from "../constants/lanes";
import {
  CONTEXT_FIELDS,
  EMPTY_CONTEXT,
  contextFromForm,
  hasRequiredContext,
} from "../constants/incidentContext";
import {
  INCIDENT_EVENTS,
  recordIncidentEvent,
} from "../services/incidentEventService";
import PaymentStep from "../components/PaymentStep";
import RegionNotice from "../components/RegionNotice";

function SubmitProblem() {
  const { currentUser, currentUserRole } = useAuth();
  const navigate = useNavigate();

  // Multi-step state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    title: "",
    problem: "",
    urgency: "Medium",
    categoryId: "",
    selectedTags: [],
    files: [],
    ...EMPTY_CONTEXT,
  });

  const [submitted, setSubmitted] = useState(false);
  const [autoTags, setAutoTags] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState({
    helpersAvailable: 0,
    typicalCost: "",
    difficulty: "",
  });

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Tracks whether the out-of-scope card is currently visible.
  // Using a ref (not state) avoids adding it to the reactive effect's deps.
  const outOfScopeShowing = useRef(false);
  const [outOfScopeInfo, setOutOfScopeInfo] = useState(null);

  // Recompute session price whenever the fields that drive tier change.
  const pricing = useMemo(
    () => calculateSessionPrice(formData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.urgency, formData.categoryId, formData.selectedTags]
  );

  // Pre-fill name & email when user is logged in
  useEffect(() => {
    // Check for pending problem from FreeDiagnosis
    const pendingProblem = localStorage.getItem('pendingProblem');
    
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        name: currentUser.displayName || currentUser.email || "",
        email: currentUser.email || "",
        categoryId: TAG_CATALOG[0]?.id || "",
        problem: pendingProblem || prev.problem || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        categoryId: TAG_CATALOG[0]?.id || "",
        problem: pendingProblem || prev.problem || "",
      }));
    }
    
    // Clear the pending problem after reading it
    if (pendingProblem) {
      localStorage.removeItem('pendingProblem');
    }
  }, [currentUser]);

  // Auto-tagging based on description
  useEffect(() => {
    if (formData.problem.length > 20) {
      const tags = analyzeTextForTags(formData.problem);
      setAutoTags(tags);
    } else {
      setAutoTags([]);
    }
  }, [formData.problem]);

  // Reactively clear the out-of-scope card once the user edits their description
  // so the trigger no longer matches — only fires when the card is showing.
  useEffect(() => {
    if (!outOfScopeShowing.current) return;
    const result = detectOutOfScope(formData);
    if (!result.isOutOfScope) {
      outOfScopeShowing.current = false;
      setOutOfScopeInfo(null);
    } else {
      setOutOfScopeInfo(result);
    }
  // formData.problem / categoryId / selectedTags are the only inputs that
  // can change the out-of-scope result — intentionally omitting the ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.problem, formData.categoryId, formData.selectedTags]);

  // Update preview data when moving to step 3
  useEffect(() => {
    if (currentStep === 3) {
      // Calculate preview data
      // Estimated resolution time intentionally omitted — the customer-facing
      // promise is the response SLA (expert on screen within 120 minutes), and
      // showing a separate multi-hour estimate here contradicted it.
      const difficulty = formData.urgency === "High" ? "High" : formData.urgency === "Medium" ? "Medium" : "Low";

      setPreviewData({
        helpersAvailable: 0,
        typicalCost: "",
        difficulty,
      });
    }
  }, [currentStep, formData.urgency]);

  // Helper function to analyze text and suggest tags
  const analyzeTextForTags = (text) => {
    const lowerText = text.toLowerCase();
    const allTags = TAG_CATALOG.flatMap((cat) => cat.tags);
    const matches = allTags.filter((tag) =>
      lowerText.includes(tag.toLowerCase().replace("#", ""))
    );
    return matches.slice(0, 5);
  };


  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setFormData((prev) => ({ ...prev, categoryId, selectedTags: [] }));
  };

  const handleToggleTag = (tag) => {
    setFormData((prev) => {
      const t = normalizePredefinedTag(tag);
      const has = prev.selectedTags.includes(t);
      const next = has
        ? prev.selectedTags.filter((x) => x !== t)
        : [...prev.selectedTags, t];
      // Limit to 10 tags (Firestore array-contains-any query limitation)
      const maxTags = 10;
      return { ...prev, selectedTags: next.slice(0, maxTags) };
    });
  };

  // File upload handlers
  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(
      (f) => f.size <= 10 * 1024 * 1024 && (f.type.startsWith("image/") || f.type.startsWith("video/"))
    );

    if (validFiles.length !== fileArray.length) {
      toast.warning("Some files were skipped (max 10MB, images/videos only)");
    }

    const newFiles = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, ...newFiles].slice(0, 5), // Max 5 files
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (fileId) => {
    setFormData((prev) => {
      const newFiles = prev.files.filter((f) => f.id !== fileId);
      const removed = prev.files.find((f) => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return { ...prev, files: newFiles };
    });
  };


  // Step validation
  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.problem.trim()) {
        toast.error("Please describe your Stripe payment incident.");
        return false;
      }

      // Out-of-scope filter — runs after we know there is a description.
      // Feedback is the inline card only (no toast).
      const oos = detectOutOfScope(formData);
      if (oos.isOutOfScope) {
        outOfScopeShowing.current = true;
        setOutOfScopeInfo(oos);
        return false;
      }
      outOfScopeShowing.current = false;
      setOutOfScopeInfo(null);

      if (!formData.categoryId) {
        toast.error("Please choose a category.");
        return false;
      }
      if (formData.selectedTags.length === 0) {
        toast.error("Please select at least one incident tag.");
        return false;
      }
      if (!hasRequiredContext(formData)) {
        toast.error(
          "Please describe the environment and what is actually happening."
        );
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Block accidental HTML form submission — all submission goes through
  // handlePaymentSuccess after Stripe confirms payment in step 4.
  const handleSubmit = (e) => {
    e.preventDefault();
  };

  /**
   * Called by PaymentStep after Stripe confirms the payment.
   * Creates the Firestore problem document with all payment fields.
   * Throws on failure so PaymentStep can display the error inline.
   */
  const handlePaymentSuccess = async ({ paymentIntentId, tier, amount }) => {
    if (!currentUser) {
      navigate("/signup");
      return;
    }

    // Convert first file to base64 attachment (same logic as handleSubmit).
    let attachment = null;
    if (formData.files.length > 0) {
      const file = formData.files[0].file;
      attachment = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    // Build normalised tags array.
    const tagsArray = Array.from(
      new Set(formData.selectedTags.map(normalizePredefinedTag))
    ).slice(0, 10);

    const diagnosisCategory = localStorage.getItem("pendingDiagnosisCategory");
    if (diagnosisCategory) {
      const categoryNameMapping = {
        "API Integration": "API & Integrations",
        "App Development": "Full-Stack App Development",
        "Cloud & DevOps": "Cloud & DevOps",
        "AI / ML Engineering": "AI / ML Engineering",
        "AI/ML Engineering": "AI / ML Engineering",
      };
      const normalizedCategory = diagnosisCategory.trim();
      const catalogCategoryLabel =
        categoryNameMapping[normalizedCategory] || normalizedCategory;
      const matchingCategory = TAG_CATALOG.find(
        (cat) => cat.label === catalogCategoryLabel
      );
      if (matchingCategory) {
        matchingCategory.tags
          .map((tag) => normalizePredefinedTag(tag))
          .slice(0, 5)
          .forEach((tag) => {
            if (!tagsArray.includes(tag)) tagsArray.push(tag);
          });
      } else {
        const fallbackTag = normalizedCategory
          .toLowerCase()
          .replace(/[& ]/g, "");
        if (!tagsArray.includes(fallbackTag)) tagsArray.push(fallbackTag);
      }
    }

    // Fetch client location from Firestore profile.
    let clientLocation = null;
    try {
      const clientDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (clientDoc.exists()) {
        clientLocation = clientDoc.data().location || null;
      }
    } catch (err) {
      console.warn("Could not fetch client location:", err);
    }

    const incidentTitle =
      formData.title ||
      formData.problem.slice(0, 50) +
        (formData.problem.length > 50 ? "..." : "");

    // Severity is resolved once, at intake, so the queue ordering a customer
    // sees never shifts underneath them if the derivation rules change later.
    const severity = deriveSeverity({
      urgency: formData.urgency,
      categoryId: formData.categoryId,
    });
    const lane = getLaneForCategory(formData.categoryId);

    // Create the Firestore problem document — payment fields included.
    const result = await createProblem({
      title:          incidentTitle,
      description:    formData.problem,
      urgency:        formData.urgency,
      severity,
      laneId:         lane?.id || null,
      clientId:       auth.currentUser.uid,
      clientName:     formData.name || currentUser.displayName || currentUser.email,
      clientEmail:    formData.email || currentUser.email,
      clientLocation,
      attachment,
      tags:           tagsArray,
      categoryId:     formData.categoryId,
      context:        contextFromForm(formData),
      // Payment fields
      paymentStatus:    "paid",
      paymentIntentId,
      paymentTier:      tier,
      amountPaid:       amount,
    });

    if (diagnosisCategory) {
      localStorage.removeItem("pendingDiagnosisCategory");
    }

    if (!result.success) {
      throw new Error(result.error || "Failed to create problem after payment.");
    }

    // Open the incident record. These are the first two entries every incident
    // has, and they are what the workspace timeline starts from.
    const actor = {
      id: auth.currentUser.uid,
      name: formData.name || currentUser.displayName || currentUser.email,
      role: "client",
    };
    await recordIncidentEvent(result.id, {
      type: INCIDENT_EVENTS.INCIDENT_REPORTED,
      actor,
      summary: `Incident reported: ${incidentTitle}`,
      metadata: {
        severity,
        laneId: lane?.id || null,
        categoryId: formData.categoryId || null,
        urgency: formData.urgency,
        tags: tagsArray,
      },
    });
    await recordIncidentEvent(result.id, {
      type: INCIDENT_EVENTS.INCIDENT_PAID,
      actor,
      summary: "Session confirmed and payment captured",
      metadata: { paymentTier: tier, amountPaid: amount, paymentIntentId },
    });

    toast.success("Incident submitted successfully.");
    localStorage.removeItem("skillsyncs_access_confirmed");
    setSubmitted(true);

    formData.files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });

    setTimeout(() => navigate("/dashboard-client"), 3000);
  };

  const steps = [
    { number: 1, label: "Incident Details", icon: FileText },
    { number: 2, label: "File Uploads",     icon: Paperclip },
    { number: 3, label: "Review",           icon: CheckCircle2 },
    { number: 4, label: "Payment",          icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <RegionNotice />
      <div className="py-4 sm:py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 relative">
          {currentUser && normalizeRole(currentUserRole) === ROLE_VALUE.requester ? (
            <button
              onClick={() => navigate('/dashboard-client')}
              className="absolute left-0 top-0 border border-slate-300 text-slate-700 bg-white px-3 sm:px-4 py-2 rounded-lg hover:bg-slate-50 transition font-medium text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">← Customer Console</span>
              <span className="sm:hidden">←</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="absolute left-0 top-0 border border-slate-300 text-slate-700 bg-white px-3 sm:px-4 py-2 rounded-lg hover:bg-slate-50 transition font-medium text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">← Home</span>
              <span className="sm:hidden">←</span>
            </button>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Report a Stripe Incident
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            Route your payment issue to a Payment Expert in minutes.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => {
              const StepIcon = step.icon;
              return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg font-semibold transition ${
                      currentStep >= step.number
                        ? "bg-blue-600 text-white"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {currentStep > step.number ? (
                      <Check className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                      <StepIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                  </div>
                  <span
                    className={`mt-1 sm:mt-2 text-xs font-medium text-center ${
                      currentStep >= step.number
                        ? "text-blue-600"
                        : "text-slate-500"
                    }`}
                  >
                    <span className="hidden sm:inline">{step.label}</span>
                    <span className="sm:hidden">{step.number}</span>
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-1 sm:mx-2 rounded ${
                      currentStep > step.number
                        ? "bg-blue-600"
                        : "bg-slate-200"
                    }`}
                  />
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          {submitted ? (
            /* Success State */
            <div className="text-center py-12">
              <div className="relative inline-block mb-6">
                <div className="w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 border-4 border-emerald-500 rounded-full animate-ping opacity-20"></div>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Incident Submitted Successfully!
              </h2>
              <p className="text-slate-600 mb-2">
                Your incident is now in the dispatch queue for expert matching.
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Estimated match time: 1-2 hours
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={() => navigate("/dashboard-client")}
                  className="bg-blue-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base"
                >
                  <span className="hidden sm:inline">View My Incidents</span>
                  <span className="sm:hidden">View Incidents</span>
                </button>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setCurrentStep(1);
                    setFormData({
                      name: currentUser?.displayName || currentUser?.email || "",
                      email: currentUser?.email || "",
                      title: "",
                      problem: "",
                      urgency: "Medium",
                      categoryId: TAG_CATALOG[0]?.id || "",
                      selectedTags: [],
                      files: [],
                      ...EMPTY_CONTEXT,
                    });
                  }}
                  className="border border-slate-300 text-slate-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-slate-50 transition font-medium text-sm sm:text-base"
                >
                  Report Another Incident
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Step 1: Incident Details */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700">
                      Your Name
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Your Name"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!!currentUser}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700">
                      Your Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Your Email"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!!currentUser}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700">
                      Incident Title (optional)
                    </label>
                    <input
                      name="title"
                      type="text"
                      placeholder="Brief summary of the Stripe issue"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700">
                      Describe the payment issue...
                    </label>
                    <textarea
                      name="problem"
                      placeholder="Describe the Stripe/payment issue in detail..."
                      className="w-full border border-slate-300 rounded-lg px-4 py-3 h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                      value={formData.problem}
                      onChange={handleChange}
                      required
                    />

                    {/* Auto-generated Tags */}
                    {autoTags.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-2 flex items-center gap-1.5">
                          <Brain className="w-3.5 h-3.5" />
                          Intelligence Suggested Incident Tags:
                        </p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {autoTags.map((tag) => {
                            const normalized = normalizePredefinedTag(tag);
                            const selected = formData.selectedTags.includes(normalized);
                            const isDisabled = !selected && formData.selectedTags.length >= 10;
                            return (
                              <button
                                type="button"
                                key={tag}
                                onClick={() => !isDisabled && handleToggleTag(tag)}
                                disabled={isDisabled}
                                className={`px-2 sm:px-3 py-1 rounded-full border text-xs transition ${
                                  selected
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : isDisabled
                                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                    : "bg-white text-slate-700 border-slate-300 hover:border-blue-500"
                                }`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">
                      Diagnostic context
                    </p>
                    <p className="text-xs text-slate-600">
                      This is what your expert sees first. The more precise it
                      is, the less time you spend repeating yourself on the call.
                    </p>
                    {CONTEXT_FIELDS.map((field) => (
                      <div key={field.key}>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          {field.label}
                          {field.required ? (
                            <span className="ml-1 text-rose-600">*</span>
                          ) : (
                            <span className="ml-1 text-slate-400">(optional)</span>
                          )}
                        </label>
                        <textarea
                          name={field.key}
                          rows={field.rows}
                          placeholder={field.placeholder}
                          className={`w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${
                            field.mono ? "font-mono" : ""
                          }`}
                          value={formData[field.key] || ""}
                          onChange={handleChange}
                          required={field.required}
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700">
                      Incident Urgency
                      <span className="text-slate-500 text-sm ml-1">
                        (How critical is this payment incident?)
                      </span>
                    </label>
                    <select
                      name="urgency"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      value={formData.urgency}
                      onChange={handleChange}
                    >
                      <option value="Low">Low - Limited billing impact</option>
                      <option value="Medium">Medium - Impacts active transactions</option>
                      <option value="High">High - Revenue blocking / production outage</option>
                      <option value="Critical">Critical - Actively losing revenue (Emergency)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700">
                      Incident Type
                    </label>
                    <select
                      name="categoryId"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 mb-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      value={formData.categoryId}
                      onChange={handleCategoryChange}
                    >
                      {TAG_CATALOG.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-slate-700">
                        Select Incident Tags
                        <span className="text-slate-500 text-sm ml-1">
                          (choose one or more)
                        </span>
                      </label>
                      {formData.categoryId ? (
                        <>
                          <div className="flex flex-wrap gap-2">
                            {(
                              TAG_CATALOG.find((c) => c.id === formData.categoryId)
                                ?.tags || []
                            ).map((tag) => {
                              const normalized = normalizePredefinedTag(tag);
                              const selected = formData.selectedTags.includes(
                                normalized
                              );
                              const isDisabled = !selected && formData.selectedTags.length >= 10;
                              return (
                                <button
                                  type="button"
                                  key={tag}
                                  onClick={() => !isDisabled && handleToggleTag(tag)}
                                  disabled={isDisabled}
                                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition ${
                                    selected
                                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                      : isDisabled
                                      ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                      : "bg-white text-slate-700 border-slate-300 hover:border-blue-500 hover:bg-blue-50"
                                  }`}
                                >
                                  {tag}
                                </button>
                              );
                            })}
                          </div>
                          {formData.selectedTags.length > 0 && (
                            <p className="mt-3 text-sm text-slate-600">
                              Selected:{" "}
                              {formData.selectedTags
                                .map((t) => `#${t}`)
                                .join(", ")}
                            </p>
                          )}
                          {formData.selectedTags.length >= 10 && (
                            <p className="mt-2 text-xs text-amber-600 font-medium flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Maximum of 10 tags reached (Firestore query limitation)
                            </p>
                          )}
                          {formData.selectedTags.length > 0 && formData.selectedTags.length < 10 && (
                            <p className="mt-2 text-xs text-slate-500">
                              You can select up to 10 tags ({10 - formData.selectedTags.length} remaining)
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">
                          Select an incident type to view available tags.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Out-of-scope inline alert — only shown after a blocked advance attempt */}
                  {outOfScopeInfo && (
                    <div className="border-l-4 border-rose-500 bg-rose-50 rounded-lg p-4 animate-fadeIn">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 text-rose-500 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-rose-800 mb-1">
                            We can't help with this one
                          </p>
                          <p className="text-sm text-rose-700 mb-3">
                            {outOfScopeInfo.reason}
                          </p>
                          <p className="text-sm text-slate-600 mb-2">
                            If your issue involves a broken webhook, failed
                            payment, or checkout error — describe that part
                            instead and we can help.
                          </p>
                          <a
                            href="/#what-we-fix"
                            className="text-sm text-blue-600 hover:underline font-medium"
                          >
                            What can SkillSyncs fix? →
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={nextStep}
                      className="bg-blue-600 text-white px-4 sm:px-6 py-2.5 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base"
                    >
                      <span className="hidden sm:inline">Continue to Evidence Upload →</span>
                      <span className="sm:hidden">Next →</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: File Uploads */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Attach Evidence (optional)
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Upload screenshots or short videos to help experts investigate faster.
                      Max 5 files, 10MB each.
                    </p>

                    {/* Drag and Drop Zone */}
                    <div
                      ref={dropZoneRef}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-12 text-center transition ${
                        dragActive
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
                      }`}
                    >
                      <Upload className="w-10 h-10 mx-auto mb-4 text-slate-400" />
                      <p className="text-slate-700 font-medium mb-2">
                        Drag and drop files here
                      </p>
                      <p className="text-sm text-slate-500 mb-4">or</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                      >
                        Choose Files
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={(e) => {
                          if (e.target.files) handleFiles(e.target.files);
                        }}
                        className="hidden"
                      />
                    </div>

                    {/* Uploaded Files Preview */}
                    {formData.files.length > 0 && (
                      <div className="mt-6 space-y-3">
                        <h4 className="text-sm font-medium text-slate-700">
                          Uploaded Files ({formData.files.length}/5)
                        </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                          {formData.files.map((fileObj) => (
                            <div
                              key={fileObj.id}
                              className="relative border border-slate-200 rounded-lg overflow-hidden group"
                            >
                              {fileObj.type.startsWith("image/") ? (
                                <img
                                  src={fileObj.preview}
                                  alt={fileObj.name}
                                  className="w-full h-32 object-cover"
                                />
                              ) : (
                                <div className="w-full h-32 bg-slate-100 flex items-center justify-center">
                                  <Video className="w-8 h-8 text-slate-400" />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeFile(fileObj.id)}
                                className="absolute top-2 right-2 bg-rose-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                              <div className="p-2 bg-white">
                                <p className="text-xs text-slate-700 truncate">
                                  {fileObj.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {(fileObj.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between pt-4 gap-3">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="border border-slate-300 text-slate-700 px-4 sm:px-6 py-2.5 rounded-lg hover:bg-slate-50 transition font-medium text-sm sm:text-base flex-1 sm:flex-none"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={nextStep}
                      className="bg-blue-600 text-white px-4 sm:px-6 py-2.5 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base flex-1 sm:flex-none"
                    >
                      <span className="hidden sm:inline">Continue to Review →</span>
                      <span className="sm:hidden">Review →</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation & Preview */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Review Incident Submission
                  </h3>

                  {/* Preview Card */}
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      Incident Summary
                    </h4>
                    <div className="mb-4">
                      <p className="text-xs text-slate-600 mb-1">Difficulty</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {previewData.difficulty}
                      </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <p className="text-xs text-slate-600 mb-2">
                        Category:{" "}
                        <span className="font-medium">
                          {
                            TAG_CATALOG.find((c) => c.id === formData.categoryId)
                              ?.label
                          }
                        </span>
                      </p>
                      <p className="text-xs text-slate-600">
                        We categorized your incident as{" "}
                        <span className="font-medium">
                          {
                            TAG_CATALOG.find((c) => c.id === formData.categoryId)
                              ?.label
                          }
                        </span>
                        . Estimated difficulty:{" "}
                        <span className="font-medium">{previewData.difficulty}</span>
                        .
                      </p>
                    </div>
                  </div>

                  {/* Problem Preview */}
                  <div className="bg-white border border-slate-200 rounded-lg p-6">
                    <h4 className="font-semibold text-slate-900 mb-3">
                      {formData.title || "Incident Preview"}
                    </h4>
                    <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">
                      {formData.problem}
                    </p>
                    <dl className="mb-4 space-y-2 border-t border-slate-100 pt-3">
                      {CONTEXT_FIELDS.map((field) =>
                        formData[field.key] ? (
                          <div key={field.key}>
                            <dt className="text-xs font-medium text-slate-500">
                              {field.label}
                            </dt>
                            <dd
                              className={`mt-0.5 whitespace-pre-wrap text-sm text-slate-800 ${
                                field.mono ? "font-mono" : ""
                              }`}
                            >
                              {formData[field.key]}
                            </dd>
                          </div>
                        ) : null
                      )}
                    </dl>
                    <div className="flex flex-wrap gap-2">
                      {formData.selectedTags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>


                  {/* Trust Banner */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
                    <Lock className="w-5 h-5 flex-shrink-0 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        Your data is secure
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        We protect your privacy and data.
                      </p>
                    </div>
                  </div>

                  {/* Session price preview */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-600 mb-0.5">Session fee</p>
                      <p className="text-base font-semibold text-slate-900">
                        {pricing.label}
                      </p>
                    </div>
                    <CreditCard className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  </div>

                  <div className="flex justify-between pt-4 gap-3">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="border border-slate-300 text-slate-700 px-4 sm:px-6 py-2.5 rounded-lg hover:bg-slate-50 transition font-medium text-sm sm:text-base flex-1 sm:flex-none"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={nextStep}
                      className="bg-blue-600 text-white px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base flex items-center justify-center gap-2 flex-1 sm:flex-none"
                    >
                      <Brain className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="hidden sm:inline">Continue to Payment →</span>
                      <span className="sm:hidden">Pay →</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Payment */}
              {currentStep === 4 && (
                <PaymentStep
                  pricing={pricing}
                  formData={formData}
                  onPaymentSuccess={handlePaymentSuccess}
                  onBack={prevStep}
                />
              )}
            </form>
          )}
        </div>
      </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default SubmitProblem;
